import app from '../../common/app';
import Model from '../Model';
import computed from '../utils/computed';
import ItemList from '../utils/ItemList';
import Badge from '../components/Badge';
export default class Discussion extends Model {
  title() {
    return Model.attribute('title').call(this);
  }
  slug() {
    return Model.attribute('slug').call(this);
  }
  createdAt() {
    return Model.attribute('createdAt', Model.transformDate).call(this);
  }
  user() {
    return Model.hasOne('user').call(this);
  }
  firstPost() {
    return Model.hasOne('firstPost').call(this);
  }
  lastPostedAt() {
    return Model.attribute('lastPostedAt', Model.transformDate).call(this);
  }
  lastPostedUser() {
    return Model.hasOne('lastPostedUser').call(this);
  }
  lastPost() {
    return Model.hasOne('lastPost').call(this);
  }
  lastPostNumber() {
    return Model.attribute('lastPostNumber').call(this);
  }
  commentCount() {
    return Model.attribute('commentCount').call(this);
  }
  replyCount() {
    return computed('commentCount', commentCount => Math.max(0, (commentCount ?? 0) - 1)).call(this);
  }
  posts() {
    return Model.hasMany('posts').call(this);
  }
  mostRelevantPost() {
    return Model.hasOne('mostRelevantPost').call(this);
  }
  lastReadAt() {
    return Model.attribute('lastReadAt', Model.transformDate).call(this);
  }
  lastReadPostNumber() {
    return Model.attribute('lastReadPostNumber').call(this);
  }
  isUnread() {
    return computed('unreadCount', unreadCount => !!unreadCount).call(this);
  }
  isRead() {
    return computed('unreadCount', unreadCount => !!(app.session.user && !unreadCount)).call(this);
  }
  hiddenAt() {
    return Model.attribute('hiddenAt', Model.transformDate).call(this);
  }
  hiddenUser() {
    return Model.hasOne('hiddenUser').call(this);
  }
  isHidden() {
    return computed('hiddenAt', hiddenAt => !!hiddenAt).call(this);
  }
  canReply() {
    return Model.attribute('canReply').call(this);
  }
  canRename() {
    return Model.attribute('canRename').call(this);
  }
  canHide() {
    return Model.attribute('canHide').call(this);
  }
  canDelete() {
    return Model.attribute('canDelete').call(this);
  }

  /**
   * Remove a post from the discussion's posts relationship.
   */
  removePost(id) {
    const posts = this.rawRelationship('posts');
    if (!posts) {
      return;
    }
    posts.some((data, i) => {
      if (id === data.id) {
        posts.splice(i, 1);
        return true;
      }
      return false;
    });
  }

  /**
   * Get the estimated number of unread posts in this discussion for the current
   * user.
   */
  unreadCount() {
    const user = app.session.user;
    if (user && (user.markedAllAsReadAt()?.getTime() ?? 0) < this.lastPostedAt()?.getTime()) {
      const unreadCount = Math.max(0, (this.lastPostNumber() ?? 0) - (this.lastReadPostNumber() || 0));
      // If posts have been deleted, it's possible that the unread count could exceed the
      // actual post count. As such, we take the min of the two to ensure this isn't an issue.
      return Math.min(unreadCount, this.commentCount() ?? 0);
    }
    return 0;
  }

  /**
   * Get the Badge components that apply to this discussion.
   */
  badges() {
    const items = new ItemList();
    if (this.isHidden()) {
      items.add('hidden', m(Badge, {
        type: "hidden",
        icon: "fas fa-trash",
        label: app.translator.trans('core.lib.badge.hidden_tooltip'),
        tabindex: "0"
      }));
    }
    return items;
  }

  /**
   * Get a list of all of the post IDs in this discussion.
   */
  postIds() {
    return this.rawRelationship('posts')?.map(link => link.id) ?? [];
  }
}
flarum.reg.add('core', 'common/models/Discussion', Discussion);